import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useFinance } from "./FinanceContext";
import { clone, now, today } from "./data/model";
import type { ApprovalDecision, BudgetItem, VersionRecord } from "./data/types";
import { StatusPill } from "./components/StatusChip";
import { toneColor } from "./components/tone";

const LEVELS: Array<"Finance Head" | "Director" | "CEO"> = ["Finance Head", "Director", "CEO"];
const ORDER = [
  "Draft",
  "Validated",
  "Finance Review",
  "Director Review",
  "CEO Review",
  "Approved",
  "Published",
];

export function ApprovalsPage() {
  const {
    staged,
    setStaged,
    model,
    setModel,
    fmt,
    role,
    has,
    setVersions,
    versions,
    setLastChange,
    setSnapshots,
    logAudit,
    setPage,
    approveRevision,
  } = useFinance();
  const [cmt, setCmt] = useState("");

  const revisions = versions.filter((v) => v.status === "Revision");

  function act(lvl: "Finance Head" | "Director" | "CEO", decision: NonNullable<ApprovalDecision>) {
    setStaged((prev) => {
      if (!prev) return prev;
      const n = clone(prev);
      n.approvals[lvl] = decision;
      if (cmt.trim())
        n.comments = [
          { who: role + " · " + lvl, text: cmt.trim(), date: now(), decision },
          ...n.comments,
        ];
      const d = LEVELS.map((l) => n.approvals[l]);
      if (d.includes("Rejected")) n.status = "Rejected";
      else if (d.every((x) => x === "Approved")) n.status = "Approved";
      else n.status = lvl === "Finance Head" ? "Director Review" : "CEO Review";
      return n;
    });
    logAudit(
      "Approval " + decision,
      (staged?.ver ?? "") + " — " + lvl + (cmt.trim() ? ` · "${cmt.trim()}"` : ""),
    );
    setCmt("");
  }

  function publish() {
    if (!staged) return;
    const snapPrev = clone(model);
    const mIdx = staged.mIdx;
    const cells: Record<string, 1> = {};
    let bC = 0;
    let aC = 0;
    let rC = 0;
    let add = 0;
    const n = clone(model);
    const byLed: Record<string, BudgetItem> = {};
    n.items.forEach((it) => (byLed[it.led] = it));
    staged.validation.rows
      .filter((r) => r._sev !== "error" && r.budgetCode)
      .forEach((r) => {
        let it = byLed[r.budgetCode];
        if (!it) {
          it = {
            id: "I" + Date.now() + Math.floor(Math.random() * 999),
            catLetter: "H",
            type: "Variable",
            sno: 99,
            code: r.budgetCode,
            led: r.budgetCode,
            name: r.particulars,
            dept: r.department || "General",
            cc: r.costCenter || "CC-H",
            planned: Array(12).fill(0),
            actual: Array(12).fill(0),
            approved: 0,
            annualPlan: 0,
          };
          n.items.push(it);
          byLed[r.budgetCode] = it;
          add++;
        }
        if (
          typeof r.budget === "number" &&
          !Number.isNaN(r.budget) &&
          Math.round(it.planned[mIdx] ?? 0) !== Math.round(r.budget)
        ) {
          it.planned[mIdx] = r.budget;
          cells[it.id + "|planned|" + mIdx] = 1;
          bC++;
        }
        if (
          typeof r.actual === "number" &&
          !Number.isNaN(r.actual) &&
          Math.round(it.actual[mIdx] ?? 0) !== Math.round(r.actual)
        ) {
          it.actual[mIdx] = r.actual;
          cells[it.id + "|actual|" + mIdx] = 1;
          aC++;
        }
      });
    (staged.parsed.revenue || [])
      .filter((rv) => typeof rv.actual === "number" && rv.actual >= 0)
      .forEach((rv) => {
        const r = n.revenue.find((x) => x.name === rv.stream);
        if (r) {
          r.actual[mIdx] = rv.actual;
          rC++;
        }
      });
    setModel(n);
    const pages = {
      dashboard: bC + aC + rC,
      budget: bC + aC,
      bva: bC + aC,
      revenue: rC,
      reports: Math.min(12, bC),
      monthly: bC + aC,
    };
    setLastChange({
      version: staged.ver,
      by: role,
      date: today(),
      cells,
      pages,
      counts: staged.diff.counts,
    });
    const publishedRecord: VersionRecord = {
      id: staged.id,
      ver: staged.ver,
      label: staged.label,
      month: staged.month,
      fy: staged.fy,
      uploadedBy: staged.uploadedBy,
      uploadDate: staged.uploadDate,
      status: "Published",
      publishedDate: now(),
      checksum: staged.checksum,
      size: staged.size,
      remarks: staged.remarks,
      reason: staged.remarks || "Tally publish",
      changeCount: staged.diff.counts.total,
      summary: staged.summary,
      snapshot: n,
      approvals: staged.approvals,
      comments: staged.comments,
      mIdx: staged.mIdx,
      parsed: staged.parsed,
      validation: staged.validation,
      diff: staged.diff,
      pages,
    };
    setVersions((vs) => [...vs, publishedRecord]);
    setSnapshots((sn) => [
      {
        id: "s" + Date.now(),
        name: "Pre-" + staged.ver + " snapshot",
        date: now(),
        ver: staged.ver,
        data: snapPrev,
      },
      ...sn,
    ]);
    logAudit(
      "Version published",
      `${staged.ver} merged — ${bC + aC + rC} cells, ${add} new lines. Transaction committed.`,
    );
    setStaged(null);
    setPage("dashboard");
  }

  const canPublish = has("approval.publish");
  const allApproved = staged ? LEVELS.every((l) => staged.approvals[l] === "Approved") : false;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Approval Workflow
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Lifecycle: Planning → Dept Review → Finance Review → Director Approval → CEO Approval →
        Approved → Frozen → Revision → Published
      </Typography>

      {revisions.length > 0 ? (
        <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Frozen-Period Revisions — Dual Approval Required ({revisions.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Old</TableCell>
                  <TableCell align="right">New</TableCell>
                  <TableCell>Approvals</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {revisions.map((v) => {
                  const ap = v.dualApprovers ?? [];
                  const mine = ap.some((x) => x.role === role);
                  const priv = role === "CEO" || role === "Director" || role === "Finance Head";
                  return (
                    <TableRow key={v.id}>
                      <TableCell sx={{ fontWeight: 700 }}>{v.ver}</TableCell>
                      <TableCell>
                        {v.change
                          ? `${v.change.name} · ${v.change.field} · ${v.change.month}`
                          : v.label}
                      </TableCell>
                      <TableCell>{v.uploadedBy}</TableCell>
                      <TableCell>{v.reason}</TableCell>
                      <TableCell align="right">{v.change ? fmt(v.change.old) : "—"}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: "action.hover" }}>
                        {v.change ? fmt(v.change.new) : "—"}
                      </TableCell>
                      <TableCell>
                        {ap.length}/2 {ap.map((x) => x.role).join(", ")}
                      </TableCell>
                      <TableCell>
                        {priv && !mine ? (
                          <Button
                            size="small"
                            color="success"
                            variant="contained"
                            onClick={() => approveRevision(v.id)}
                          >
                            Approve
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {mine ? "You approved" : "Privileged roles only"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}

      {!staged || staged.status === "Published" ? (
        <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No Tally version pending. Upload a file to start the workflow.
          </Typography>
          <Button variant="contained" sx={{ mt: 1.5 }} onClick={() => setPage("upload")}>
            Go to Tally Import →
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {staged.ver} · {staged.month} · by {staged.uploadedBy} · checksum {staged.checksum}
          </Typography>
          <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mb: 2 }}>
            {ORDER.map((s) => {
              const done = ORDER.indexOf(staged.status) > ORDER.indexOf(s);
              const isCur = staged.status === s;
              return (
                <Chip
                  key={s}
                  size="small"
                  label={(done ? "✓ " : "") + s}
                  color={done ? "success" : isCur ? "primary" : "default"}
                  variant={done || isCur ? "filled" : "outlined"}
                />
              );
            })}
            {staged.status === "Rejected" ? (
              <Chip size="small" color="error" label="✕ Rejected" />
            ) : null}
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 1.5,
              mb: 2,
            }}
          >
            {(
              [
                ["Rows Added", staged.diff.counts.added, "success.main"],
                ["Rows Modified", staged.diff.counts.modified, "primary.main"],
                ["Unchanged", staged.diff.counts.unchanged, undefined],
                ["Errors", staged.summary.error, "error.main"],
                ["Warnings", staged.summary.warning, "warning.main"],
              ] as [string, number, string | undefined][]
            ).map(([label, value, color]) => (
              <Box key={label} sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              Multi-level Approval
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Level</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {LEVELS.map((lvl) => (
                    <TableRow key={lvl}>
                      <TableCell>{lvl}</TableCell>
                      <TableCell>
                        <StatusPill status={staged.approvals[lvl] ?? "Pending"} />
                      </TableCell>
                      <TableCell>
                        {staged.status !== "Rejected" &&
                        staged.status !== "Approved" &&
                        !staged.approvals[lvl] ? (
                          role === lvl ? (
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                color="success"
                                variant="contained"
                                onClick={() => act(lvl, "Approved")}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant="contained"
                                onClick={() => act(lvl, "Rejected")}
                              >
                                Reject
                              </Button>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Switch role to &quot;{lvl}&quot;
                            </Typography>
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {staged.approvals[lvl] ?? "—"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Comment"
              value={cmt}
              onChange={(e) => setCmt(e.target.value)}
              placeholder="e.g. Approved — consistent with headcount plan"
              sx={{ mt: 1.5 }}
            />
            {staged.comments.length > 0 ? (
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {staged.comments.map((c, i) => (
                  <Box key={i} sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {c.who} · {c.date} ·{" "}
                      <StatusPill status={c.decision === "Approved" ? "Approved" : "Rejected"} />
                    </Typography>
                    <Typography variant="body2">{c.text}</Typography>
                  </Box>
                ))}
              </Stack>
            ) : null}
            <Box sx={{ mt: 1.5 }}>
              {allApproved && staged.status === "Approved" ? (
                <Button variant="contained" disabled={!canPublish} onClick={publish}>
                  Publish to Production
                </Button>
              ) : staged.status === "Rejected" ? (
                <Typography variant="body2" color="text.secondary">
                  Rejected — retained for audit.
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Current vs Uploaded (modified)
              </Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Cost Head</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell align="right">Current</TableCell>
                      <TableCell align="right">Uploaded</TableCell>
                      <TableCell align="right">Δ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staged.diff.changes
                      .filter((c) => c.type === "modified")
                      .slice(0, 30)
                      .map((c, i) => (
                        <TableRow key={i}>
                          <TableCell>{c.code}</TableCell>
                          <TableCell>{c.name}</TableCell>
                          <TableCell>{c.field}</TableCell>
                          <TableCell align="right">{fmt(c.old)}</TableCell>
                          <TableCell align="right" sx={{ bgcolor: "action.hover" }}>
                            {fmt(c.new)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: toneColor(c.new - c.old >= 0 ? "pos" : "neg") }}
                          >
                            {(c.new - c.old >= 0 ? "+" : "") + fmt(c.new - c.old)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                New lines (added)
              </Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Cost Head</TableCell>
                      <TableCell align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staged.diff.changes
                      .filter((c) => c.type === "added")
                      .map((c, i) => (
                        <TableRow key={i}>
                          <TableCell>{c.code}</TableCell>
                          <TableCell>{c.name}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ bgcolor: "success.main", color: "success.contrastText" }}
                          >
                            {fmt(c.new)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
